import React, { useState, useEffect } from 'react';
import {
    ExclamationTriangleIcon,
    PhotoIcon,
    XMarkIcon,
    CheckCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { ReportSubmission, ReportedResource, ReportCategory } from '../../types/report';
import { reportService } from '../../services/reportService';
import SelectDropdown, { SelectOption } from '../ui/SelectDropdown';

interface ReportFormProps {
    reportedResource: ReportedResource;
    onSubmit?: (success: boolean) => void;
    onCancel?: () => void;
}

export const ReportForm: React.FC<ReportFormProps> = ({
    reportedResource,
    onSubmit,
    onCancel
}) => {
    const [categories, setCategories] = useState<ReportCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const [formData, setFormData] = useState<ReportSubmission>({
        reportedResource,
        category: 'other',
        reason: '',
        description: '',
        evidence: {
            screenshots: [],
            additionalInfo: ''
        }
    });

    const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await reportService.getReportCategories();
            if (response.success && response.data && response.data.length > 0) {
                setCategories(response.data);
                const firstCategory = response.data[0];
                if (firstCategory?.value) {
                    setFormData(prev => ({
                        ...prev,
                        category: firstCategory.value as ReportCategory['value']
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleInputChange = (field: keyof ReportSubmission, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleEvidenceChange = (field: keyof NonNullable<ReportSubmission['evidence']>, value: any) => {
        setFormData(prev => ({
            ...prev,
            evidence: {
                ...prev.evidence,
                [field]: value
            }
        }));
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        const maxFiles = 5;
        const maxFileSize = 5 * 1024 * 1024; // 5MB

        const validFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) {
                setError('Only image files are allowed');
                return false;
            }
            if (file.size > maxFileSize) {
                setError('File size must be less than 5MB');
                return false;
            }
            return true;
        });

        const totalFiles = evidenceFiles.length + validFiles.length;
        if (totalFiles > maxFiles) {
            setError(`You can only upload up to ${maxFiles} images`);
            return;
        }

        setEvidenceFiles(prev => [...prev, ...validFiles]);
        setError(null);
    };

    const removeFile = (index: number) => {
        setEvidenceFiles(prev => prev.filter((_, i) => i !== index));

        // Also remove from uploaded URLs if they exist
        if (formData.evidence?.screenshots?.[index]) {
            setFormData(prev => ({
                ...prev,
                evidence: {
                    ...prev.evidence,
                    screenshots: prev.evidence?.screenshots?.filter((_, i) => i !== index) || []
                }
            }));
        }
    };

    const uploadFiles = async () => {
        if (evidenceFiles.length === 0) return true;

        setUploading(true);
        try {
            const response = await reportService.uploadEvidence(evidenceFiles);
            if (response.success && response.data) {
                handleEvidenceChange('screenshots', response.data);
                return true;
            } else {
                setError(response.error || 'Failed to upload images');
                return false;
            }
        } catch (error) {
            setError('Failed to upload images');
            return false;
        } finally {
            setUploading(false);
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.category) {
            errors.category = 'Please select a category';
        }

        if (!formData.reason.trim()) {
            errors.reason = 'Please provide a reason for this report';
        } else if (formData.reason.trim().length < 10) {
            errors.reason = 'Reason must be at least 10 characters long';
        } else if (formData.reason.trim().length > 200) {
            errors.reason = 'Reason cannot exceed 200 characters';
        }

        if (!formData.description.trim()) {
            errors.description = 'Please provide a detailed description';
        } else if (formData.description.trim().length < 20) {
            errors.description = 'Description must be at least 20 characters long';
        } else if (formData.description.trim().length > 1000) {
            errors.description = 'Description cannot exceed 1000 characters';
        }

        if (formData.evidence?.additionalInfo && formData.evidence.additionalInfo.length > 500) {
            errors.additionalInfo = 'Additional information cannot exceed 500 characters';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Upload files if any
            const uploadSuccess = await uploadFiles();
            if (!uploadSuccess) {
                setLoading(false);
                return;
            }

            // Submit the report
            const response = await reportService.submitReport(formData);

            if (response.success) {
                setSuccess('Report submitted successfully. We will review it and take appropriate action.');
                setTimeout(() => {
                    onSubmit?.(true);
                }, 2000);
            } else {
                if (response.errors && Array.isArray(response.errors)) {
                    const fieldErrors: Record<string, string> = {};
                    response.errors.forEach((error: any) => {
                        if (error.field) {
                            fieldErrors[error.field] = error.message;
                        }
                    });
                    setValidationErrors(fieldErrors);
                }
                setError(response.error || 'Failed to submit report');
                onSubmit?.(false);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An unexpected error occurred');
            onSubmit?.(false);
        } finally {
            setLoading(false);
        }
    };

    const selectedCategory = categories.find(cat => cat.value === formData.category);

    return (
        <div className="w-full p-4 sm:p-6">
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 sm:p-6">
                <div className="flex items-center mb-6 gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 flex-shrink-0" />
                    <div className="min-w-0">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                            Report {reportedResource.type.charAt(0).toUpperCase() + reportedResource.type.slice(1)}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Help us maintain a safe and respectful community
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-3 sm:p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md">
                        <div className="flex gap-2 sm:gap-3">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="text-sm text-red-600 dark:text-red-200 break-words">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="mb-6 p-3 sm:p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-md">
                        <div className="flex gap-2 sm:gap-3">
                            <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="text-sm text-green-600 dark:text-green-200 break-words">{success}</p>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                    {/* Category */}
                    <div>
                        <SelectDropdown
                            label="Report Category *"
                            value={formData.category}
                            onChange={(value) => handleInputChange('category', value)}
                            options={categories.map((category): SelectOption => ({
                                value: category.value,
                                label: category.label
                            }))}
                            placeholder="Select a category"
                            className={validationErrors.category ? 'border-red-300 dark:border-red-600' : ''}
                        />
                        {validationErrors.category && (
                            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{validationErrors.category}</p>
                        )}
                        {selectedCategory && (
                            <p className="mt-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {selectedCategory.description}
                            </p>
                        )}
                    </div>

                    {/* Reason */}
                    <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Brief Reason * <span className="text-xs text-gray-500">({formData.reason.length}/200)</span>
                        </label>
                        <input
                            type="text"
                            id="reason"
                            value={formData.reason}
                            onChange={(e) => handleInputChange('reason', e.target.value)}
                            placeholder="Provide a brief reason for this report..."
                            maxLength={200}
                            className={`block w-full px-3 py-2.5 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${validationErrors.reason
                                ? 'border-red-300 dark:border-red-600'
                                : 'border-gray-300 dark:border-gray-600'
                                }`}
                        />
                        {validationErrors.reason && (
                            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{validationErrors.reason}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Detailed Description * <span className="text-xs text-gray-500">({formData.description.length}/1000)</span>
                        </label>
                        <textarea
                            id="description"
                            rows={4}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Please provide detailed information about why you're reporting this. Include specific examples and context to help us understand the issue..."
                            maxLength={1000}
                            className={`block w-full px-3 py-2.5 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none scrollbar-hide ${validationErrors.description
                                ? 'border-red-300 dark:border-red-600'
                                : 'border-gray-300 dark:border-gray-600'
                                }`}
                        />
                        {validationErrors.description && (
                            <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{validationErrors.description}</p>
                        )}
                    </div>

                    {/* Evidence Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Evidence (Optional)
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 sm:p-4">
                            <div className="text-center">
                                <PhotoIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                                <div className="mt-2">
                                    <label htmlFor="evidence-upload" className="cursor-pointer">
                                        <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                                            Upload Screenshots (Max 5, 5MB each)
                                        </span>
                                        <input
                                            id="evidence-upload"
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleFileSelect}
                                            className="sr-only"
                                        />
                                        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                                            PNG, JPG, GIF up to 5MB each
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* File Preview */}
                            {evidenceFiles.length > 0 && (
                                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {evidenceFiles.map((file, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Evidence ${index + 1}`}
                                                className="h-20 w-full object-cover rounded"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="absolute -top-2 -right-2 bg-red-500 dark:bg-red-600 text-white dark:text-gray-100 rounded-full p-1 hover:bg-red-600 dark:hover:bg-red-700 transition-colors"
                                            >
                                                <XMarkIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Additional Info */}
                        <div className="mt-4">
                            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Additional Information <span className="text-xs text-gray-500">({(formData.evidence?.additionalInfo || '').length}/500)</span>
                            </label>
                            <textarea
                                id="additionalInfo"
                                rows={2}
                                value={formData.evidence?.additionalInfo || ''}
                                onChange={(e) => handleEvidenceChange('additionalInfo', e.target.value)}
                                placeholder="Any additional context or information..."
                                maxLength={500}
                                className={`block w-full px-3 py-2.5 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none scrollbar-hide ${validationErrors.additionalInfo
                                    ? 'border-red-300 dark:border-red-600'
                                    : 'border-gray-300 dark:border-gray-600'
                                    }`}
                            />
                            {validationErrors.additionalInfo && (
                                <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{validationErrors.additionalInfo}</p>
                            )}
                        </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-md p-3 sm:p-4">
                        <div className="flex gap-2 sm:gap-3">
                            <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-sm text-blue-700 dark:text-blue-200">
                                    <strong>Please note:</strong> We take all reports seriously and will investigate them thoroughly.
                                    False reports may result in action against your account. All reports are confidential and
                                    will be reviewed by our moderation team.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-5 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || uploading}
                            className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? (uploading ? 'Uploading...' : 'Submitting...') : 'Submit Report'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};